/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { connect } from 'react-redux';
import gql from 'graphql-tag';
import { Query } from 'react-apollo';
import {
    number, array, arrayOf, string, func, bool, shape,
} from 'prop-types';
import mbpLogger from 'mbp-logger';
import { GRAPHQL_ENV } from '../../../../../gql';
import { getBrand } from '../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import GraphqlAddon from './GraphqlAddon';
import { addToCartDataType } from '../common/types';

const brandSelectors = getBrand;
// multiple network calls are needed for the building structure because the error page is a category page but with some special copy from the 404 content page.
const GraphqlAddonContainer = ({
    brand,
    product,
    skuData,
    selectedAddons,
    handleAddonChange,
    product: {
        brandId,
        partNumber,
    },
    categoryId,
    subscription,
    subscriptionDetails,
    presentation_family,
    disableAddToCart,
    isAddToCartEnabled,
    categoryPath,
    categoryName,
    selectedSku,
    productSkus,
    updateWineStatus,
    deliveryData,
    handleMiniCartModalClick,
    mobileAgeVerificationOpen,
    handleMobileAgeVerification,
    addToCartData,
}) => {
    const ADD_ON_PAGE_QUERY = gql`
    query PDPpageQuery {
        findContent(brand: "${brand.domain}", environment: "${GRAPHQL_ENV}", contentType: "addon_banner")
        {
          content
        }
       }
    `;
    return (
        <Query query={ADD_ON_PAGE_QUERY}>
            {({ loading, error, data }) => {
                if (loading) {
                    return null; // skeleton component
                }

                if (error) {
                    mbpLogger.logError({
                        query: ADD_ON_PAGE_QUERY,
                        component: 'GraphqlAddonContainer.js',
                        message: 'Error loading data from Graphql',
                        env: GRAPHQL_ENV,
                        error,
                    });
                    return null;
                }

                if (!data) {
                    mbpLogger.logWarning({
                        query: ADD_ON_PAGE_QUERY,
                        component: 'GraphqlAddonContainer.js',
                        message: 'No data returned for query',
                        env: GRAPHQL_ENV,
                    });
                    return null;
                }

                if (data <= 0) {
                    mbpLogger.logWarning({
                        query: ADD_ON_PAGE_QUERY,
                        component: 'GraphqlAddonContainer.js',
                        message: 'No content returned for query',
                        env: GRAPHQL_ENV,
                    });
                    return null;
                }
                return (
                    <GraphqlAddon
                        handleMiniCartModalClick={handleMiniCartModalClick}
                        brand={brand}
                        selectedAddons={selectedAddons}
                        pageData={data}
                        product={product}
                        skuData={skuData}
                        handleAddonChange={handleAddonChange}
                        brandId={brandId}
                        partNumber={partNumber}
                        categoryId={categoryId}
                        subscription={subscription}
                        subscriptionDetails={subscriptionDetails}
                        presentation_family={presentation_family}
                        disableAddToCart={disableAddToCart}
                        isAddToCartEnabled={isAddToCartEnabled}
                        categoryPath={categoryPath}
                        categoryName={categoryName}
                        selectedSku={selectedSku}
                        productSkus={productSkus}
                        updateWineStatus={updateWineStatus}
                        deliveryData={deliveryData}
                        mobileAgeVerificationOpen={mobileAgeVerificationOpen}
                        handleMobileAgeVerification={handleMobileAgeVerification}
                        addToCartData={addToCartData}
                    />
                );
            }}
        </Query>
    );
};

GraphqlAddonContainer.propTypes = {
    brand: shape({
        domain: string,
    }).isRequired,
    product: shape({
        id: string.isRequired,
        name: string.isRequired,
        brandId: string.isRequired,
        partNumber: string.isRequired,
    }).isRequired,
    skuData: arrayOf(
        shape({
            id: string.isRequired,
            name: string.isRequired,
            prices: arrayOf(
                shape({
                    type: string.isRequired,
                    value: number.isRequired,
                }),
            ).isRequired,
        }),
    ).isRequired,
    selectedAddons: arrayOf(string),
    handleAddonChange: func,
    categoryId: string.isRequired,
    subscriptionDetails: shape({
        subscriptionType: string,
        subscriptionDuration: string,
        subscriptionFrequency: string,
    }),
    subscription: shape({
        subscriptionKey: array,
        subscriptionIndefinite: string,
        subscriptionType: array,
        implementProductSubscription: bool,
        viewSubscription: bool,
        isTypeDiscovery: bool,
    }),
    presentation_family: string.isRequired,
    disableAddToCart: bool,
    isAddToCartEnabled: bool,
    categoryPath: string.isRequired,
    categoryName: string.isRequired,
    selectedSku: shape({
        id: string.isRequired,
        minAge: string.isRequired,
    }).isRequired,
    productSkus: shape({
        minAge: string.isRequired,
    }).isRequired,
    deliveryData: shape({
        location_type_options: arrayOf(
            shape({
                name: string,
                value: string,
            }),
        ),
        show: bool,
        title: string,
        zip_code_title: string,
        location_type_title: string,
    }).isRequired,
    updateWineStatus: func.isRequired,
    handleMiniCartModalClick: func.isRequired,
    mobileAgeVerificationOpen: bool,
    handleMobileAgeVerification: func,
    addToCartData: addToCartDataType,
};

GraphqlAddonContainer.defaultProps = {
    selectedAddons: [],
    handleAddonChange: () => {},
    isAddToCartEnabled: true,
    subscriptionDetails: {},
    disableAddToCart: false,
    subscription: {},
    mobileAgeVerificationOpen: false,
    handleMobileAgeVerification: () => { },
    addToCartData: {},
};

const mapStateToProps = (state) => ({
    brand: brandSelectors(state),
});

export default connect(mapStateToProps)(GraphqlAddonContainer);
